import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth';
import { usePageHeaderStore } from '../stores/pageHeader';
import {
  Building2,
  ChevronRight,
  Sparkles,
  ArrowUpRight,
  FileText,
  Clock,
  Layers,
  MoreVertical
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';

interface Organization {
  id: string;
  name: string;
  slug: string;
  projectCount?: number;
}

interface RecentRequest {
  id: string;
  projectId: string;
  projectName: string;
  projectKey: string;
  orgId: string;
  epicId: string | null;
  epicTitle: string | null;
  title: string;
  status: string;
  createdAt: string;
}

interface Epic {
  id: string;
  title: string;
  projectId: string;
}

// Status badge colors
const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'var(--muted)',
  READY: 'var(--info)',
  ENRICHED: 'var(--primary)',
  WIREFRAME_READY: 'var(--primary)',
  GENERATED: 'var(--success)',
  IN_REVIEW: 'var(--warning)',
  APPROVED: 'var(--success)',
  REJECTED: 'var(--error)',
  DONE: 'var(--success)',
  ERROR: 'var(--error)',
};

// Request row with epic assignment dropdown
function RecentRequestRow({ request }: { request: RecentRequest }) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  
  // Fetch epics for the request's project
  const { data: epics } = useQuery({
    queryKey: ['project-epics-dropdown', request.projectId],
    queryFn: () => api.get<{ data: Epic[] }>(`/epics?projectId=${request.projectId}`),
    enabled: showMenu,
  });
  
  // Mutation to update request's epic
  const updateEpicMutation = useMutation({
    mutationFn: async (epicId: string | null) => {
      return api.patch(`/requests/${request.id}`, { epicId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recent-requests'] });
      setShowMenu(false);
    },
  });
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);
  
  const statusColor = STATUS_COLORS[request.status] || 'var(--muted)';
  
  return (
    <div className="flex items-center justify-between py-3 hover:bg-surface-2 -mx-5 px-5 transition-all rounded-md group">
      <Link
        to={`/app/organizations/${request.orgId}/projects/${request.projectId}/requests/${request.id}`}
        className="flex items-center gap-3 flex-1 min-w-0"
      >
        <div 
          className="w-10 h-10 rounded-card flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--primary-dim)' }}
        >
          <FileText className="w-5 h-5" style={{ color: 'var(--primary)' }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-default group-hover:text-primary transition-colors truncate">
            {request.title}
          </p>
          <div className="flex items-center gap-2 text-sm text-muted">
            <span>{request.projectKey}</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(request.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      </Link>
      
      <div className="flex items-center gap-3">
        {/* Status badge */}
        <span 
          className="px-2 py-0.5 text-xs font-medium rounded-full"
          style={{ 
            background: `color-mix(in srgb, ${statusColor} 15%, transparent)`,
            color: statusColor 
          }}
        >
          {request.status.replace('_', ' ')}
        </span>
        
        {/* Epic assignment */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={(e) => {
              e.preventDefault();
              setShowMenu(!showMenu);
            }}
            className={clsx(
              "flex items-center gap-1.5 px-2 py-1 text-sm rounded-md transition-colors",
              request.epicId 
                ? "text-muted hover:bg-surface-3" 
                : "text-primary hover:bg-primary-dim"
            )}
          >
            <Layers className="w-3.5 h-3.5" />
            <span className="max-w-[100px] truncate">
              {request.epicTitle || 'Add to Epic'}
            </span>
            <MoreVertical className="w-3.5 h-3.5" />
          </button>
          
          {showMenu && (
            <div 
              className="absolute right-0 top-full mt-1 w-56 rounded-lg shadow-lg border z-50 py-1 max-h-64 overflow-y-auto"
              style={{ background: 'var(--surface-1)', borderColor: 'var(--border)' }}
            >
              <div className="px-3 py-2 text-xs font-medium text-muted uppercase tracking-wide">
                Move to Epic
              </div>
              {epics?.data.map((epic) => (
                <button
                  key={epic.id}
                  onClick={() => updateEpicMutation.mutate(epic.id)}
                  className={clsx(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors",
                    epic.id === request.epicId 
                      ? "bg-primary-dim text-primary" 
                      : "hover:bg-surface-2 text-default"
                  )}
                >
                  <Layers className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{epic.title}</span>
                </button>
              ))}
              {(!epics?.data || epics.data.length === 0) && (
                <div className="px-3 py-4 text-sm text-muted text-center">
                  No epics in this project
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { user } = useAuthStore();
  const { setHeader } = usePageHeaderStore();
  
  const { data: orgs, isLoading } = useQuery({
    queryKey: ['orgs'],
    queryFn: () => api.get<{ data: Organization[]; pagination: { total: number } }>('/orgs'),
  });
  
  // Fetch recent requests (last 5)
  const { data: recentRequests, isLoading: isLoadingRequests } = useQuery({
    queryKey: ['recent-requests'],
    queryFn: () => api.get<{ data: RecentRequest[] }>('/requests?limit=5&sortBy=createdAt&sortOrder=desc'),
  });
  
  useEffect(() => {
    const firstName = user?.fullName ? user.fullName.split(' ')[0] : '';
    setHeader(
      `Welcome back${firstName ? `, ${firstName}` : ''}`
    );
  }, [user, setHeader]);
  
  return (
    <div className="space-y-8 animate-fade-in">
      {/* Organizations list */}
      <div className="card">
        <div className="card-header justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md flex items-center justify-center"
              style={{ background: 'var(--primary-dim)' }}
            >
              <Building2 className="w-4 h-4" style={{ color: 'var(--primary)' }} />
            </div>
            <h3 className="card-title">Your Organizations</h3>
          </div>
          <Link to="/app/organizations" className="btn btn-ghost text-sm gap-1">
            View all
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 py-3">
                <div className="skeleton w-10 h-10 rounded-card" />
                <div className="flex-1">
                  <div className="skeleton h-4 w-1/3 mb-2 rounded" />
                  <div className="skeleton h-3 w-1/4 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
            {orgs?.data.slice(0, 5).map((org, index) => (
              <Link
                key={org.id}
                to={`/app/organizations/${org.id}`}
                className="flex items-center justify-between py-4 hover:bg-surface-2 -mx-5 px-5 transition-all rounded-md group"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-card flex items-center justify-center"
                    style={{ background: 'var(--primary-dim)' }}
                  >
                    <Building2 className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                  </div>
                  <div>
                    <p className="font-medium text-default group-hover:text-primary transition-colors">
                      {org.name}
                    </p>
                    <p className="text-sm text-muted">@{org.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="badge badge-neutral">
                    {org.projectCount ?? 0} projects
                  </span>
                  <ChevronRight className="w-5 h-5 text-subtle group-hover:text-primary group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            ))}
            
            {orgs?.data.length === 0 && (
              <div className="empty-state">
                <div className="empty-state-icon mx-auto">
                  <Building2 className="w-full h-full" />
                </div>
                <p className="empty-state-title">No organizations yet</p>
                <p className="empty-state-text">Create your first organization to get started</p>
                <Link to="/app/organizations" className="btn btn-primary">
                  <Sparkles className="w-4 h-4" />
                  Create Organization
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Recent Requests */}
      <div className="card">
        <div className="card-header justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md flex items-center justify-center"
              style={{ background: 'var(--info-dim)' }}
            >
              <Clock className="w-4 h-4" style={{ color: 'var(--info)' }} />
            </div>
            <h3 className="card-title">Recent Requests</h3>
          </div>
        </div>
        
        {isLoadingRequests ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3 py-3">
                <div className="skeleton w-10 h-10 rounded-card" />
                <div className="flex-1">
                  <div className="skeleton h-4 w-1/2 mb-2 rounded" />
                  <div className="skeleton h-3 w-1/3 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
            {recentRequests?.data.slice(0, 5).map((request) => (
              <RecentRequestRow 
                key={request.id} 
                request={request}
              />
            ))}
            
            {(!recentRequests?.data || recentRequests.data.length === 0) && (
              <div className="empty-state py-8">
                <div className="empty-state-icon mx-auto">
                  <FileText className="w-full h-full" />
                </div>
                <p className="empty-state-title">No requests yet</p>
                <p className="empty-state-text">
                  Requests created from the Chrome extension will appear here
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      
    </div>
  );
}
